/**
 * This file is kinda the root for possible circle dependencies. Use `import type...` extensively to avoid them.
 */
import type { EventBusKey } from '@vueuse/core'
import type { InjectionKey, Ref } from 'vue'
import type { GanttContext } from '~/composables/gantt/ganttContext'
import type { ActivityContext } from '~/composables/activityContext'
import type { AppSettingsComposable } from '~/composables/app-settings/provideAppSettings'
import type { ReportContext } from '~/composables/report/reportContext'
import type { EquipmentsRepository } from '~/composables/repositories/equipmentsRepository'
import type { SwitchingsRepository } from '~/composables/repositories/switchingsRepository'
import type { ProjectsRepository } from '~/composables/repositories/projectsRepository'
import type { SubProjectsRepository } from '~/composables/repositories/subProjectsRepository'
import type { SwitchingPremisesRepository } from '~/composables/repositories/switchingPremisesRepository'
import type { SBlockRestrictionsRepository } from '~/composables/repositories/switchingBlockRestrictionsRepository'
import { isTest } from '~/lib/constants'
import type { SinaSurveyContext } from '~/composables/sinaSurveyContext'
import type { SinaContext } from '~/composables/sinaContext'
import type { SinaProjectsRepository } from '~/composables/repositories/sina/sinaProjectsRepository'
import type { SurveysRepository } from '~/composables/repositories/sina/surveysRepository'
import type { JobClusterRepository } from '~/composables/repositories/sina/jobClusterRepository'
import type { ResultRepository } from '~/composables/repositories/sina/resultsRepository'
import type { ConfirmDialogHandler } from '~/plugins/vue-plugins/use-dialog'
import type { FetchErrorHandler } from '~/plugins/vue-plugins/fetch-error-handler'
import type { SwitchingWindowsRepository } from '~/composables/repositories/sina/switchingWindowsRepository'
import type { GotenbergService } from '~/lib/services/export/gotenberg'
import type { MatrixController } from '~/composables/matrix/useMatrixController'
import type { TimelineContext } from '~/composables/sina/timelineContext'

export const ActivityDataImportSymbol: EventBusKey<boolean> = Symbol.for('ActivityDataImportSymbol')
export const FetchGanttDataBusSymbol = Symbol.for('FetchGanttDataBusSymbol')
export const ActivityContextSymbol = Symbol.for('ActivityContextSymbol') as InjectionKey<ActivityContext>
export const SinaContextSymbol = Symbol.for('SinaContextSymbol') as InjectionKey<SinaContext>
export const SinaSurveyContextSymbol = Symbol.for('SinaSurveyContextSymbol') as InjectionKey<SinaSurveyContext>
export const GanttContextKey = Symbol.for('GanttContextKey') as InjectionKey<GanttContext>
export const ProjectsRepositorySymbol = Symbol.for('ProjectsRepositorySymbol') as InjectionKey<ProjectsRepository>
export const SubProjectsRepositorySymbol = Symbol.for(
  'SubProjectsRepositorySymbol',
) as InjectionKey<SubProjectsRepository>
export const EquipmentsRepositorySymbol = Symbol.for('EquipmentsRepositorySymbol') as InjectionKey<EquipmentsRepository>
export const SwitchingsRepositorySymbol = Symbol.for('SwitchingsRepositorySymbol') as InjectionKey<SwitchingsRepository>
export const SBlockRestrictionsRepositorySymbol = Symbol.for(
  'SBlockRestrictionsRepositorySymbol',
) as InjectionKey<SBlockRestrictionsRepository>
export const SwitchingPremisesRepositorySymbol = Symbol.for(
  'SwitchingPremisesRepositorySymbol',
) as InjectionKey<SwitchingPremisesRepository>
export const AppSettingsKey = Symbol.for('AppSettingsKey') as InjectionKey<AppSettingsComposable>
export const GanttBarsContainerSymbol = Symbol.for('GanttBarsContainerSymbol') as InjectionKey<Ref<DOMRect>>
export const ReportContextSymbol = Symbol.for('ReportContextSymbol') as InjectionKey<ReportContext>
export const SinaProjectsRepositorySymbol = Symbol.for(
  'SinaProjectsRepositorySymbol',
) as InjectionKey<SinaProjectsRepository>
export const SinaSurveysRepositorySymbol = Symbol.for('SinaSurveysRepositorySymbol') as InjectionKey<SurveysRepository>
export const SinaJobClusterRepositorySymbol = Symbol.for(
  'SinaJobClusterRepositorySymbol',
) as InjectionKey<JobClusterRepository>
export const SinaResultRepositorySymbol = Symbol.for('SinaResultRepositorySymbol') as InjectionKey<ResultRepository>
export const SinaSwitchingWindowsRepositorySymbol = Symbol.for(
  'SinaSwitchingWindowsRepositorySymbol',
) as InjectionKey<SwitchingWindowsRepository>
export const TimelineContextSymbol = Symbol.for('TimelineContextSymbol') as InjectionKey<TimelineContext>

export const MatrixControllerSymbol = Symbol.for('MatrixControllerSymbol') as InjectionKey<MatrixController>
export const HandleFetchErrorKey = Symbol.for('HandleFetchErrorKey') as InjectionKey<FetchErrorHandler>
export const ConfirmDialogKey = Symbol.for('ConfirmDialogKey') as InjectionKey<ConfirmDialogHandler>
export const GotenbergServiceKey = Symbol.for('GotenbergService') as InjectionKey<GotenbergService>

/**
 * In a test environment using symbols does not work, that's why we provide strings
 */
export function getKey<T>(name: string) {
  return isTest ? name : (Symbol.for(name) as InjectionKey<T>)
}

/**
 * Type Helper
 */
export type InjectType<U> = U extends InjectionKey<infer T> ? T : never
